import {
	LegalLayout,
	type LegalSection,
} from '#app/components/marketing/legal-layout.tsx'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/tos.ts'

export const meta: Route.MetaFunction = ({ matches }) =>
	buildMeta({
		title: 'Terms of Service - Syrcha Privacy Compliance Scanner',
		description:
			'Terms of Service for Syrcha privacy compliance scanning. Read the usage rules, billing terms, and legal disclaimers.',
		matches,
	})

export default function TermsOfServiceRoute() {
	const sections: LegalSection[] = [
		{
			id: 'eligibility',
			title: 'Eligibility',
			body: (
				<>
					<p>You may use Syrcha only if you:</p>
					<ul>
						<li>
							Are at least 18 years old (or the age of legal majority in your
							jurisdiction).
						</li>
						<li>
							Have the authority to bind your company or organization (if using
							on behalf of a business).
						</li>
						<li>Comply with all applicable laws and regulations.</li>
					</ul>
				</>
			),
		},
		{
			id: 'registration',
			title: 'Account Registration',
			body: (
				<ul>
					<li>
						You must provide accurate and complete information when creating an
						account.
					</li>
					<li>
						You are responsible for maintaining the confidentiality of your
						login credentials.
					</li>
					<li>You are responsible for all activity under your account.</li>
					<li>
						Notify us immediately of any unauthorized access or security breach.
					</li>
				</ul>
			),
		},
		{
			id: 'use-services',
			title: 'Use of Services',
			body: (
				<>
					<p>You agree not to:</p>
					<ul>
						<li>Use the Services for unlawful or unauthorized purposes.</li>
						<li>
							Scan, test, or audit third-party websites or apps without proper
							authorization.
						</li>
						<li>
							Interfere with or disrupt the integrity or performance of the
							Services.
						</li>
						<li>
							Reverse-engineer, copy, or resell the Services without our written
							consent.
						</li>
					</ul>
					<p>
						We reserve the right to suspend or terminate accounts that violate
						these rules.
					</p>
				</>
			),
		},
		{
			id: 'subscriptions',
			title: 'Subscription Plans & Payments',
			body: (
				<ul>
					<li>
						Certain features are free; others require a paid subscription.
					</li>
					<li>
						Paid plans are billed on a recurring basis (monthly or annually).
					</li>
					<li>All fees are non-refundable except as required by law.</li>
					<li>
						Prices are subject to change, but we will notify you in advance.
					</li>
					<li>
						Payments are processed by Stripe. Card details are held by Stripe,
						not by us.
					</li>
				</ul>
			),
		},
		{
			id: 'trial',
			title: 'Trial & Free Use',
			body: (
				<p>
					We may offer free plans or trial access. We reserve the right to
					modify or discontinue free access at any time without notice.
				</p>
			),
		},
		{
			id: 'availability',
			title: 'Service Availability',
			body: (
				<ul>
					<li>
						We strive to maintain 99.9% uptime but do not guarantee
						uninterrupted access.
					</li>
					<li>Scheduled maintenance and unforeseen downtime may occur.</li>
					<li>
						We are not liable for any losses due to service interruptions.
					</li>
				</ul>
			),
		},
		{
			id: 'privacy',
			title: 'Data & Privacy',
			body: (
				<ul>
					<li>
						Your use of the Services is subject to our{' '}
						<a href="/privacy">Privacy Policy</a>.
					</li>
					<li>
						By using Syrcha, you agree that we may process your data as
						described therein.
					</li>
					<li>
						You retain all rights to data you upload or provide, but grant us a
						limited license to process and analyze it for providing the
						Services.
					</li>
				</ul>
			),
		},
		{
			id: 'ip',
			title: 'Intellectual Property',
			body: (
				<ul>
					<li>
						Syrcha and all related trademarks, logos, software, and content are
						owned by us or our licensors.
					</li>
					<li>
						You are granted a limited, non-exclusive, non-transferable license
						to use the Services.
					</li>
					<li>
						You may not copy, modify, distribute, or create derivative works
						without permission.
					</li>
				</ul>
			),
		},
		{
			id: 'third-parties',
			title: 'Third-Party Services',
			body: (
				<p>
					Our Services may integrate with third-party platforms (e.g., Slack,
					GitHub, Jira). Use of those services is subject to their own terms.
				</p>
			),
		},
		{
			id: 'termination',
			title: 'Termination',
			body: (
				<>
					<p>We may suspend or terminate your account if you:</p>
					<ul>
						<li>Violate these Terms.</li>
						<li>Fail to pay subscription fees.</li>
						<li>Misuse or abuse the Services.</li>
					</ul>
					<p>
						Upon termination, your right to use the Services ends immediately,
						but sections relating to intellectual property, limitations of
						liability, and indemnity will survive.
					</p>
				</>
			),
		},
		{
			id: 'disclaimers',
			title: 'Disclaimers',
			body: (
				<ul>
					<li>
						The Services are provided{' '}
						<strong>"as is" and "as available."</strong>
					</li>
					<li>
						We do not guarantee that compliance scans will identify all issues.
					</li>
					<li>
						We disclaim all warranties, express or implied, including
						merchantability and fitness for a particular purpose.
					</li>
				</ul>
			),
		},
		{
			id: 'liability',
			title: 'Limitation of Liability',
			body: (
				<>
					<p>To the maximum extent permitted by law:</p>
					<ul>
						<li>
							Syrcha is not liable for indirect, incidental, or consequential
							damages.
						</li>
						<li>
							Our total liability will not exceed the amount you paid to us in
							the past 12 months.
						</li>
					</ul>
				</>
			),
		},
		{
			id: 'indemnification',
			title: 'Indemnification',
			body: (
				<>
					<p>
						You agree to indemnify and hold Syrcha harmless from claims,
						damages, or expenses arising out of:
					</p>
					<ul>
						<li>Your misuse of the Services.</li>
						<li>Your violation of these Terms.</li>
						<li>Your violation of any laws or third-party rights.</li>
					</ul>
				</>
			),
		},
		{
			id: 'law',
			title: 'Governing Law & Dispute Resolution',
			body: (
				<>
					<p>
						Syrcha is operated from Australia. Our servers are in the EU; the
						company is not. Those are two separate facts and this section is
						about the second one.
					</p>
					<ul>
						<li>These Terms are governed by the laws of Australia.</li>
						<li>
							Disputes will be resolved through binding arbitration or courts
							located in Australia.
						</li>
						<li>
							If you are a consumer in the EU, EEA, or UK, your local consumer
							law still applies and nothing here removes it, including your
							statutory right to bring a claim in your own country's courts.
						</li>
					</ul>
				</>
			),
		},
		{
			id: 'changes',
			title: 'Changes to Terms',
			body: (
				<p>
					We may update these Terms from time to time. Updates will be posted on
					this page with the "Last Updated" date. Continued use of the Services
					constitutes acceptance of changes.
				</p>
			),
		},
		{
			id: 'contact',
			title: 'Contact Us',
			body: (
				<>
					<p>For questions about these Terms, contact us at:</p>
					<blockquote>
						<p>
							<strong>Syrcha Legal Team</strong>
						</p>
						<p>
							Email: <a href="mailto:legal@syrcha.com">legal@syrcha.com</a>
						</p>
					</blockquote>
				</>
			),
		},
	]

	return (
		<LegalLayout
			eyebrow="/ legal · terms"
			title={
				<>
					Terms of <span className="accent-underline">Service</span>.
				</>
			}
			effectiveDate="2025-09-01"
			lastUpdatedDate="2026-08-07"
			intro={
				<>
					<p>
						These Terms govern your use of Syrcha: the website, the scanner, the
						API, the CLI, and the extensions. Using any of them means agreeing
						to what follows.
					</p>
					<p>
						The two clauses that matter most in practice: you may only scan
						sites you own or have permission to scan (
						<a href="/aup">acceptable use</a>), and you can cancel from your
						settings at any time without asking us (
						<a href="/refund-policy">refunds</a>).
					</p>
				</>
			}
			sections={sections}
		/>
	)
}
